import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { extractCount } from '@/utils/supabaseUtils';
import { getYouTubeThumbnailWebpUrl } from '@/utils/imageUtils';
import { eventKeys } from '@/lib/queryKeys';

/**
 * Event type from Supabase
 */
export interface Event {
  id: string;
  slug?: string | null;
  title: string;
  description: string | null;
  type: 'auto_run' | 'community' | 'flagship' | 'private' | 'youtube_livestream' | 'youtube';
  event_source?: string | null;
  event_subtype?: string | null;
  event_type?: string | null;
  access: 'free' | 'public' | 'private' | 'premium';
  status: 'scheduled' | 'live' | 'starting_soon' | 'completed' | 'ended' | 'cancelled';
  db_status?: 'scheduled' | 'live' | 'starting_soon' | 'completed' | 'ended' | 'cancelled';
  start_at: string;
  end_at: string;
  thumbnail_url: string | null;
  hero_thumbnail_url?: string | null;
  youtube_url: string | null;
  youtube_video_id: string | null;
  /** 'journal-party-live' marks the weekly party; set by the YouTube sync or an admin. */
  series_key?: string | null;
  /** True until YouTube schedules the real stream for that week. */
  is_placeholder?: boolean | null;
  /** Server verdict: true = ambient background stream (hidden), false = a show, null = not judged. */
  is_ambient_stream?: boolean | null;
  capacity: number | null;
  typical_attendance_min?: number | null;
  typical_attendance_max?: number | null;
  created_by: string | null;
  program_id: string | null;
  created_at: string;
  // Signature event data (Story 4.5)
  is_signature?: boolean;
  /** @deprecated Use is_signature instead */
  is_flagship?: boolean;
  template_id?: string | null;       // Actual DB field
  signature_template_id?: string | null;  // Alias
  /** @deprecated Use signature_template_id instead */
  flagship_template_id?: string | null;  // Legacy alias
  signature_time_slot?: string | null; // e.g., "06:00 PT"
  /** @deprecated Use signature_time_slot instead */
  flagship_time_slot?: string | null; // e.g., "06:00 PT"
  event_timezone?: string | null;    // IANA timezone for this event instance
  // Lighthouse events (Epic 2.3) - featured for new users
  is_lighthouse?: boolean;
  // Joined data
  rsvp_count?: number;
  host_name?: string;
  visibility?: 'public' | 'private' | null;
  creator_id?: string | null;
  creator_profile_id?: string | null;
  creator?: {
    id: string | null;
    profile_id: string | null;
    display_name: string | null;
    avatar_url: string | null;
    is_admin: boolean | null;
  } | null;
  [key: string]: any;
}

const EVENT_LIST_FIELDS = `
  id,
  slug,
  title,
  description,
  type,
  event_source,
  event_subtype,
  access,
  status,
  start_at,
  end_at,
  thumbnail_url,
  hero_thumbnail_url,
  youtube_url,
  youtube_video_id,
  typical_attendance_min,
  typical_attendance_max,
  created_by,
  program_id,
  created_at,
  is_signature,
  template_id,
  event_timezone,
  is_lighthouse,
  visibility
`;

// Live series columns (journal-party-live) and the ambient-stream verdict.
// The web deploys on merge while the migration that adds them runs by hand
// afterwards, so for a few minutes PostgREST can reject a select that names
// them. The first such rejection
// flips this flag and the query is retried with the legacy field list.
let seriesFieldsAvailable = true;

function isUndefinedSeriesColumnError(error: unknown): boolean {
  const record = (error ?? {}) as { code?: unknown; message?: unknown };
  const message = typeof record.message === 'string' ? record.message : '';
  return (
    record.code === '42703' ||
    (/series_key|is_placeholder|is_ambient_stream/.test(message) && /does not exist|could not find/i.test(message))
  );
}

function eventListSelectBase(): string {
  const seriesFields = seriesFieldsAvailable ? ', series_key, is_placeholder, is_ambient_stream' : '';
  return `
  ${EVENT_LIST_FIELDS}${seriesFields},
  signature_event_templates!template_id(thumbnail_url),
  programs!program_id(thumbnail_url)
`;
}

function eventListSelectWithRsvps(): string {
  return `
  ${eventListSelectBase()},
  event_rsvps(count)
`;
}

function resolveEventCapacity(event: any): number | null {
  if (typeof event?.capacity === 'number') return event.capacity;
  if (typeof event?.typical_attendance_max === 'number') return event.typical_attendance_max;
  return null;
}

interface CreatorProfileRow {
  id: string;
  user_id: string | null;
  display_name: string | null;
  avatar_url: string | null;
  is_admin: boolean | null;
}

const PROFILE_LOOKUP_CHUNK_SIZE = 100;

function chunkValues<T>(values: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
}

async function fetchProfilesForIds(
  field: 'user_id' | 'id',
  ids: string[],
): Promise<{ rows: CreatorProfileRow[]; hadSuccess: boolean }> {
  const chunks = chunkValues(ids, PROFILE_LOOKUP_CHUNK_SIZE);
  const rows: CreatorProfileRow[] = [];
  let hadSuccess = false;

  for (const chunk of chunks) {
    const { data, error } = await supabase
      .from('member_profiles')
      .select('id, user_id, display_name, avatar_url, is_admin')
      .in(field, chunk);

    if (error) continue;
    hadSuccess = true;
    rows.push(...((data as CreatorProfileRow[] | null) || []));
  }

  return { rows, hadSuccess };
}

async function attachEventCreators(events: Event[]): Promise<Event[]> {
  if (events.length === 0) return events;

  const normalized = events.map((event) => ({
    ...event,
    creator_id: event.creator_id ?? event.created_by ?? null,
    creator_profile_id: event.creator_profile_id ?? null,
    creator: event.creator ?? null,
  }));

  const creatorIds = [
    ...new Set(
      normalized
        .map((event) => event.created_by ?? event.creator_id ?? null)
        .filter(Boolean)
    ),
  ] as string[];

  if (creatorIds.length === 0) return normalized;

  let byUserIdRows: CreatorProfileRow[] = [];
  let byProfileIdRows: CreatorProfileRow[] = [];
  let byUserIdSuccess = false;
  let byProfileIdSuccess = false;

  try {
    const [byUserIdResult, byProfileIdResult] = await Promise.all([
      fetchProfilesForIds('user_id', creatorIds),
      fetchProfilesForIds('id', creatorIds),
    ]);

    byUserIdRows = byUserIdResult.rows;
    byProfileIdRows = byProfileIdResult.rows;
    byUserIdSuccess = byUserIdResult.hadSuccess;
    byProfileIdSuccess = byProfileIdResult.hadSuccess;
  } catch {
    return normalized;
  }

  if (!byUserIdSuccess && !byProfileIdSuccess) {
    return normalized;
  }

  const profiles: CreatorProfileRow[] = [
    ...byUserIdRows,
    ...byProfileIdRows,
  ];

  if (profiles.length === 0) return normalized;

  const profileByUserId = new Map<string, CreatorProfileRow>();
  const profileById = new Map<string, CreatorProfileRow>();

  profiles.forEach((profile) => {
    if (profile.user_id) profileByUserId.set(profile.user_id, profile);
    if (profile.id) profileById.set(profile.id, profile);
  });

  return normalized.map((event) => {
    const creatorKey = event.created_by ?? event.creator_id ?? null;
    const profile = creatorKey
      ? profileByUserId.get(creatorKey) ?? profileById.get(creatorKey) ?? null
      : null;

    const creatorId = profile?.user_id ?? event.creator_id ?? event.created_by ?? null;
    const creatorProfileId = profile?.id ?? event.creator_profile_id ?? null;
    const creator = creatorId
      ? {
          id: creatorId,
          profile_id: creatorProfileId,
          display_name: profile?.display_name ?? null,
          avatar_url: profile?.avatar_url ?? null,
          is_admin: profile?.is_admin ?? null,
        }
      : null;

    return {
      ...event,
      creator_id: creatorId,
      creator_profile_id: creatorProfileId,
      creator,
    };
  });
}

/**
 * Grouped signature event for timezone selection (Story 4.5)
 */
export interface SignatureGroup {
  templateId: string;
  templateName: string;
  thumbnailUrl?: string;
  description?: string;
  /** RRULE recurrence pattern from template (e.g., "FREQ=DAILY", "FREQ=WEEKLY;BYDAY=MO,WE,FR") */
  recurrenceRule?: string;
  instances: Event[];
}

interface UseEventsOptions {
  status?: 'scheduled' | 'live' | 'completed' | 'ended' | 'all';
  limit?: number;
  includePast?: boolean;
  /** Filter to only lighthouse events (Epic 2.3 - featured for new users) */
  isLighthouse?: boolean;
  /** Filter to YouTube stream events only */
  youtubeOnly?: boolean;
  /** Date range filter for calendar views - includes past events within range */
  dateRange?: { start: Date; end: Date };
  /** Include RSVP counts via event_rsvps join (heavier query) */
  includeRsvpCount?: boolean;
  /** Attach creator profiles (extra profile lookups) */
  includeCreator?: boolean;
  /** Sort by start_at ascending (default true). */
  sortAscending?: boolean;
  /** Disable query until ready */
  enabled?: boolean;
}

/**
 * Derive the effective event status based on current time
 *
 * The database status may lag behind real-time, so we compute the
 * effective status client-side based on start_at/end_at times.
 *
 * Status transitions:
 * - 'scheduled' + now >= start_at && now < end_at → 'live'
 * - 'scheduled' + now < start_at && within 15 min → 'starting_soon'
 * - 'scheduled' or 'live' + now >= end_at → 'ended'
 * - 'cancelled' always stays 'cancelled'
 * - 'completed' always stays 'completed'
 */
function deriveEventStatus(
  dbStatus: Event['status'],
  startAt: string,
  endAt: string
): Event['status'] {
  // Cancelled, completed, and ended are final states
  if (dbStatus === 'cancelled' || dbStatus === 'completed' || dbStatus === 'ended') {
    return dbStatus;
  }

  const now = Date.now();
  const startTime = new Date(startAt).getTime();
  const endTime = new Date(endAt).getTime();

  // Event has ended
  if (now >= endTime) {
    return 'ended';
  }

  // Event is currently live (started but not ended)
  if (now >= startTime && now < endTime) {
    return 'live';
  }

  // Event starts within 15 minutes
  const fifteenMinutes = 15 * 60 * 1000;
  if (now >= startTime - fifteenMinutes && now < startTime) {
    return 'starting_soon';
  }

  // Still scheduled (future event)
  return 'scheduled';
}

export function isYouTubeEvent(event: Pick<Event, 'type' | 'youtube_url'>): boolean {
  return event.type === 'youtube_livestream' || event.type === 'youtube' || !!event.youtube_url;
}

/**
 * Derive UI status while preserving server truth for YouTube streams that may run
 * much longer than their initial scheduled duration.
 */
export function deriveDisplayEventStatus(
  event: Pick<Event, 'status' | 'db_status' | 'start_at' | 'end_at' | 'type' | 'youtube_url'>
): Event['status'] {
  const sourceStatus = event.db_status ?? event.status;

  if (sourceStatus === 'cancelled' || sourceStatus === 'completed' || sourceStatus === 'ended') {
    return sourceStatus;
  }

  if (isYouTubeEvent(event) && sourceStatus === 'live') {
    return 'live';
  }

  return deriveEventStatus(sourceStatus, event.start_at, event.end_at);
}

/**
 * Fetch upcoming and live events
 *
 * Thumbnail resolution (in priority order):
 * 1. Event's own thumbnail_url → use it
 * 2. Signature template's thumbnail → inherit from template
 * 3. Program's thumbnail → inherit from affiliated program
 *
 * This ensures events display appropriate thumbnails even without custom uploads,
 * and template/program thumbnail changes apply to all associated events.
 */
async function fetchEvents(options: UseEventsOptions): Promise<Event[]> {
  const {
    status = 'all',
    limit = 20,
    includePast = false,
    isLighthouse,
    youtubeOnly = false,
    dateRange,
    includeRsvpCount = true,
    includeCreator = true,
    sortAscending = true,
  } = options;

  // Join with signature_event_templates and programs to get fallback thumbnails
  let query: any = supabase
    .from('events')
    .select(includeRsvpCount ? eventListSelectWithRsvps() : eventListSelectBase())
    .order('start_at', { ascending: sortAscending })
    .limit(limit);

  // Filter by status
  if (status === 'live') {
    query = query.eq('status', 'live');
  } else if (status === 'scheduled') {
    query = query.eq('status', 'scheduled');
  } else if (status !== 'all') {
    query = query.in('status', ['scheduled', 'live']);
  }

  // Date range filtering for calendar views - takes precedence over includePast
  if (dateRange) {
    // Include all events that overlap with the date range
    // Event overlaps if: event.start_at < range.end AND event.end_at > range.start
    query = query
      .lt('start_at', dateRange.end.toISOString())
      .gt('end_at', dateRange.start.toISOString());
  } else if (!includePast) {
    // Only future events unless includePast
    // Filter by end_at > now (event hasn't ended yet)
    // This ensures we don't show events that have already completed
    query = query.gte('end_at', new Date().toISOString());
  }

  // Only show public events in discovery feeds
  query = query.eq('visibility', 'public');

  // Filter lighthouse events (Epic 2.3)
  if (isLighthouse !== undefined) {
    query = query.eq('is_lighthouse', isLighthouse);
  }

  // YouTube-only filter for stream-centric surfaces
  if (youtubeOnly) {
    query = query.or('type.eq.youtube_livestream,type.eq.youtube,youtube_url.not.is.null');
  }

  const { data, error } = await query;
  if (error && seriesFieldsAvailable && isUndefinedSeriesColumnError(error)) {
    seriesFieldsAvailable = false;
    return fetchEvents(options);
  }

  if (error) throw error;

  const mappedEvents = ((data || []) as any[]).map(event => {
    const rsvpCount = extractCount((event as any).event_rsvps);

    // Resolve thumbnail: event's own > template's > program's
    const templateThumbnail = (event.signature_event_templates as { thumbnail_url: string | null } | null)?.thumbnail_url;
    const programThumbnail = (event.programs as { thumbnail_url: string | null } | null)?.thumbnail_url;
    const resolvedThumbnail = event.thumbnail_url || templateThumbnail || programThumbnail || null;
    const optimizedResolvedThumbnail =
      getYouTubeThumbnailWebpUrl(resolvedThumbnail, event.youtube_video_id) || resolvedThumbnail;

    const templateId = (event as any).template_id ?? null;
    const signatureTemplateId = (event as any).signature_template_id ?? templateId;
    const flagshipTemplateId = (event as any).flagship_template_id ?? templateId;
    const signatureTimeSlot = (event as any).signature_time_slot ?? (event as any).flagship_time_slot ?? null;
    const flagshipTimeSlot = (event as any).flagship_time_slot ?? signatureTimeSlot ?? null;
    const isFlagship = (event as any).is_flagship ?? (event as any).is_signature ?? false;
    return {
      ...event,
      type: event.type as Event['type'],
      access: event.access as Event['access'],
      status: deriveDisplayEventStatus({
        status: event.status as Event['status'],
        start_at: event.start_at,
        end_at: event.end_at,
        type: event.type as Event['type'],
        youtube_url: event.youtube_url,
      }),
      db_status: event.status as Event['status'],
      capacity: resolveEventCapacity(event),
      template_id: templateId,
      signature_template_id: signatureTemplateId,
      flagship_template_id: flagshipTemplateId,
      signature_time_slot: signatureTimeSlot,
      flagship_time_slot: flagshipTimeSlot,
      is_flagship: isFlagship,
      creator_id: (event as any).creator_id ?? event.created_by ?? null,
      creator_profile_id: null,
      creator: null,
      thumbnail_url: optimizedResolvedThumbnail,
      rsvp_count: Number.isFinite(rsvpCount) ? rsvpCount : 0,
    };
  }) as unknown as Event[];

  if (!includeCreator) {
    return mappedEvents;
  }

  return attachEventCreators(mappedEvents);
}

/**
 * Fetch a single event by ID
 * Inherits thumbnail from template if event doesn't have its own
 */
export async function fetchEventById(eventId: string): Promise<Event | null> {
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      event_rsvps(count),
      signature_event_templates!template_id(thumbnail_url),
      programs!program_id(thumbnail_url)
    `)
    .eq('id', eventId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  // Resolve thumbnail: event's own > template's > program's
  const templateThumbnail = (data.signature_event_templates as { thumbnail_url: string | null } | null)?.thumbnail_url;
  const programThumbnail = (data.programs as { thumbnail_url: string | null } | null)?.thumbnail_url;
  const resolvedThumbnail = data.thumbnail_url || templateThumbnail || programThumbnail || null;
  const optimizedResolvedThumbnail =
    getYouTubeThumbnailWebpUrl(resolvedThumbnail, data.youtube_video_id) || resolvedThumbnail;

  const rsvpCount = extractCount((data as any).event_rsvps);

  const templateId = (data as any).template_id ?? null;
  const signatureTemplateId = (data as any).signature_template_id ?? templateId;
  const flagshipTemplateId = (data as any).flagship_template_id ?? templateId;
  const signatureTimeSlot = (data as any).signature_time_slot ?? (data as any).flagship_time_slot ?? null;
  const flagshipTimeSlot = (data as any).flagship_time_slot ?? signatureTimeSlot ?? null;
  const isFlagship = (data as any).is_flagship ?? (data as any).is_signature ?? false;
  const event: Event = {
    ...data,
    type: data.type as Event['type'],
    access: data.access as Event['access'],
    status: deriveDisplayEventStatus({
      status: data.status as Event['status'],
      start_at: data.start_at,
      end_at: data.end_at,
      type: data.type as Event['type'],
      youtube_url: data.youtube_url,
    }),
    db_status: data.status as Event['status'],
    capacity: resolveEventCapacity(data),
    template_id: templateId,
    signature_template_id: signatureTemplateId,
    flagship_template_id: flagshipTemplateId,
    signature_time_slot: signatureTimeSlot,
    flagship_time_slot: flagshipTimeSlot,
    is_flagship: isFlagship,
    creator_id: (data as any).creator_id ?? data.created_by ?? null,
    creator_profile_id: null,
    creator: null,
    thumbnail_url: optimizedResolvedThumbnail,
    rsvp_count: Number.isFinite(rsvpCount) ? rsvpCount : 0,
  } as unknown as Event;

  const [eventWithCreator] = await attachEventCreators([event]);
  return eventWithCreator ?? event;
}

/**
 * Hook to fetch upcoming/live events
 */
export function useEvents(options: UseEventsOptions = {}) {
  const { enabled = true, ...queryOptions } = options;
  return useQuery({
    queryKey: eventKeys.list(queryOptions),
    queryFn: () => fetchEvents(queryOptions),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch signature template recurrence rule
 */
async function fetchTemplateRecurrence(templateId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('signature_event_templates')
    .select('recurrence_rule')
    .eq('id', templateId)
    .single();

  if (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching template recurrence:', error);
    }
    return null;
  }
  return data?.recurrence_rule ?? null;
}

/**
 * Hook to fetch a signature template's recurrence rule
 */
export function useSignatureTemplateRecurrence(templateId: string | undefined) {
  return useQuery({
    queryKey: ['signatureTemplateRecurrence', templateId],
    queryFn: () => fetchTemplateRecurrence(templateId!),
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000, // 5 minutes - recurrence rules don't change often
  });
}

/**
 * Parse RRULE into human-readable schedule description
 * Supports common patterns: DAILY, WEEKLY with BYDAY
 * Handles both "RRULE:FREQ=..." and "FREQ=..." formats
 */
export function parseRecurrenceRule(rrule: string | null | undefined): string | null {
  if (!rrule) return null;

  // Strip RRULE: prefix if present
  const rule = rrule.startsWith('RRULE:') ? rrule.slice(6) : rrule;

  // Parse RRULE components
  const parts = rule.split(';').reduce((acc, part) => {
    const [key, value] = part.split('=');
    acc[key] = value;
    return acc;
  }, {} as Record<string, string>);

  const freq = parts.FREQ;
  const byDay = parts.BYDAY;

  if (freq === 'DAILY') {
    return 'Every day';
  }

  if (freq === 'WEEKLY' && byDay) {
    const dayMap: Record<string, string> = {
      MO: 'Mon',
      TU: 'Tue',
      WE: 'Wed',
      TH: 'Thu',
      FR: 'Fri',
      SA: 'Sat',
      SU: 'Sun',
    };

    const days = byDay.split(',').map((d) => dayMap[d] || d);

    // Check for common patterns
    const daySet = new Set(byDay.split(','));

    if (daySet.size === 5 && !daySet.has('SA') && !daySet.has('SU')) {
      return 'Weekdays (Mon-Fri)';
    }
    if (daySet.size === 2 && daySet.has('SA') && daySet.has('SU')) {
      return 'Weekends (Sat & Sun)';
    }
    if (daySet.size === 7) {
      return 'Every day';
    }

    return days.join(', ');
  }

  if (freq === 'WEEKLY') {
    return 'Weekly';
  }

  if (freq === 'MONTHLY') {
    return 'Monthly';
  }

  // Fallback: return the raw rule if we can't parse it
  return null;
}

/**
 * Group signature events by template for timezone selection (Story 4.5)
 *
 * Signature events like "Morning Pages" run at multiple times to accommodate
 * different timezones. This groups them so users can pick their preferred time.
 */
export function groupSignatureEvents(events: Event[]): {
  signatureGroups: SignatureGroup[];
  regularEvents: Event[];
} {
  // Signature events are identified by is_signature=true (or legacy is_flagship) and have a template_id
  const signatureEvents = events.filter(
    (e) => (e.is_signature || e.is_flagship) && e.template_id
  );
  const regularEvents = events.filter(
    (e) => !(e.is_signature || e.is_flagship) || !e.template_id
  );

  // Group by template ID only (not by date)
  // This consolidates all times (today, tomorrow, etc.) into a single card per signature event
  const groupMap = new Map<string, Event[]>();

  for (const event of signatureEvents) {
    const key = event.template_id!;

    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)!.push(event);
  }

  const signatureGroups: SignatureGroup[] = [];

  for (const [key, instances] of groupMap) {
    if (instances.length === 0) continue;

    // Sort instances by start time
    instances.sort(
      (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
    );

    // Use the first instance as the representative
    const representative = instances[0];

    // Always treat signature events as groups (even with 1 instance)
    // This maintains visual consistency in the UI - the card always shows
    // the time dropdown, even if there's only one time slot remaining

    signatureGroups.push({
      templateId: representative.template_id!,
      templateName: representative.title.replace(/ \(.*\)$/, ''), // Remove timezone suffix
      thumbnailUrl: representative.thumbnail_url || undefined,
      description: representative.description || undefined,
      instances,
    });
  }

  return { signatureGroups, regularEvents };
}

/**
 * Format time slot for display (Story 4.5)
 * Shows time with date context: Today/Tomorrow for near events, or "Sat 12/21" for further out
 *
 * Times are displayed in the user's local timezone for clarity.
 * Per enhance-party-discoverability proposal: No timezone abbreviations shown.
 * Users select based on their local time - the time difference itself distinguishes slots.
 */
export function formatTimeSlot(event: Event): string {
  const date = new Date(event.start_at);
  const now = new Date();

  // Use local timezone for all time displays (user's browser timezone)
  // No timezone parameter = uses browser's local timezone
  const timeStr = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  // Determine day prefix using local timezone
  const isToday = date.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  let dayPrefix = '';
  if (isToday) {
    dayPrefix = 'Today ';
  } else if (isTomorrow) {
    dayPrefix = 'Tomorrow ';
  } else {
    const weekday = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
    }).format(date);
    const monthDay = new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: 'numeric',
    }).format(date);
    dayPrefix = `${weekday} ${monthDay} `;
  }

  // No timezone suffix - all times shown in user's local timezone
  // Users distinguish slots by the different local times, not timezone labels
  return dayPrefix + timeStr;
}

/**
 * Hook to fetch events hosted/created by a specific user
 * Used for creator content management
 */
async function fetchUserHostedEvents(userId: string): Promise<Event[]> {
      const { data, error } = await supabase
        .from('events')
        .select(eventListSelectWithRsvps())
        .eq('created_by', userId)
        .order('start_at', { ascending: false });

      if (error && seriesFieldsAvailable && isUndefinedSeriesColumnError(error)) {
        seriesFieldsAvailable = false;
        return fetchUserHostedEvents(userId);
      }
      if (error) throw error;

      const mappedEvents = ((data || []) as any[]).map(event => {
        const rsvpCount = extractCount((event as any).event_rsvps);

        // Resolve thumbnail: event's own > template's > program's
        const templateThumbnail = (event.signature_event_templates as { thumbnail_url: string | null } | null)?.thumbnail_url;
        const programThumbnail = (event.programs as { thumbnail_url: string | null } | null)?.thumbnail_url;
        const resolvedThumbnail = event.thumbnail_url || templateThumbnail || programThumbnail || null;
        const optimizedResolvedThumbnail =
          getYouTubeThumbnailWebpUrl(resolvedThumbnail, event.youtube_video_id) || resolvedThumbnail;

        const templateId = (event as any).template_id ?? null;
        const signatureTemplateId = (event as any).signature_template_id ?? templateId;
        const flagshipTemplateId = (event as any).flagship_template_id ?? templateId;
        const signatureTimeSlot = (event as any).signature_time_slot ?? (event as any).flagship_time_slot ?? null;
        const flagshipTimeSlot = (event as any).flagship_time_slot ?? signatureTimeSlot ?? null;
        const isFlagship = (event as any).is_flagship ?? (event as any).is_signature ?? false;
        return {
          ...event,
          type: event.type as Event['type'],
          access: event.access as Event['access'],
          status: event.status as Event['status'],
          capacity: resolveEventCapacity(event),
          template_id: templateId,
          signature_template_id: signatureTemplateId,
          flagship_template_id: flagshipTemplateId,
          signature_time_slot: signatureTimeSlot,
          flagship_time_slot: flagshipTimeSlot,
          is_flagship: isFlagship,
          creator_id: (event as any).creator_id ?? event.created_by ?? null,
          creator_profile_id: null,
          creator: null,
          thumbnail_url: optimizedResolvedThumbnail,
          rsvp_count: Number.isFinite(rsvpCount) ? rsvpCount : 0,
        };
      }) as unknown as Event[];

      return attachEventCreators(mappedEvents);
}

export function useUserHostedEvents(userId: string | undefined) {
  return useQuery({
    queryKey: ['userHostedEvents', userId],
    queryFn: async () => (userId ? fetchUserHostedEvents(userId) : ([] as Event[])),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}

// ─────────────────────────────────────────────────
// Party Page Filtering Utilities (Story 2.1)
// ─────────────────────────────────────────────────

type TimeOfDay = 'morning' | 'afternoon' | 'evening';
export type EventTypeLabel = 'powerprompt' | 'youtube' | 'user-created';
export type FilterType = 'all' | TimeOfDay | 'my-rsvps' | 'daily-events' | 'following' | 'hosting';

// Tab-based filter groups for Calendar page
export type CalendarTab = 'my-calendar' | 'explore';

/**
 * Get the appropriate filter for a calendar tab
 * - "my-calendar": Combined view of RSVPs + Following
 * - "explore": All events (discovery)
 */
export function getDefaultFilterForTab(tab: CalendarTab): FilterType {
  return tab === 'my-calendar' ? 'my-rsvps' : 'all';
}

/**
 * Filter events by time of day (in user's local timezone)
 * - Morning: before 12pm
 * - Afternoon: 12pm - 5pm
 * - Evening: after 5pm
 */
function filterByTimeOfDay(events: Event[], timeOfDay: TimeOfDay): Event[] {
  return events.filter(event => {
    const hour = new Date(event.start_at).getHours();
    switch (timeOfDay) {
      case 'morning':
        return hour < 12;
      case 'afternoon':
        return hour >= 12 && hour < 17;
      case 'evening':
        return hour >= 17;
      default:
        return true;
    }
  });
}

/**
 * Filter events to only those the user has RSVP'd to
 * Optionally includes events from subscribed series (minus skipped instances)
 *
 * @param subscribedTemplateTimezones - Map of template_id → subscribed timezone
 *   Only includes events whose timezone matches the user's subscribed timezone
 */
function filterByRSVP(
  events: Event[],
  rsvpEventIds: Set<string>,
  subscribedTemplateTimezones?: Map<string, string>,
  skippedEventIds?: Set<string>,
): Event[] {
  return events.filter((event) => {
    // Exclude skipped events first
    if (skippedEventIds?.has(event.id)) return false;

    // Include if directly RSVP'd
    if (rsvpEventIds.has(event.id)) return true;

    // Include if from a subscribed series AND matches subscribed timezone
    if (
      subscribedTemplateTimezones &&
      event.template_id &&
      subscribedTemplateTimezones.has(event.template_id)
    ) {
      const subscribedTz = subscribedTemplateTimezones.get(event.template_id);
      // If event has a timezone, only include if it matches subscribed timezone
      if (event.event_timezone && subscribedTz) {
        return event.event_timezone === subscribedTz;
      }
      // Fallback: include if no timezone info available
      return true;
    }

    return false;
  });
}

/**
 * Filter to signature/daily events only
 */
function filterBySignature(events: Event[]): Event[] {
  return events.filter(event => event.is_signature || event.is_flagship || event.template_id);
}

/**
 * Filter to events created by followed users
 * Always includes signature events (they have no specific creator)
 */
function filterByFollowing(events: Event[], followedUserIds: Set<string>): Event[] {
  return events.filter(event => {
    // Always include signature events (they're for everyone)
    if (event.is_signature || event.is_flagship || event.template_id) return true;
    // Include if created by someone the user follows
    return event.created_by && followedUserIds.has(event.created_by);
  });
}

/**
 * Filter to events created/hosted by a specific user
 */
function filterByHosting(events: Event[], userId: string): Event[] {
  return events.filter(event => event.created_by === userId);
}

/**
 * Apply a filter type to events
 *
 * @param subscribedTemplateTimezones - Map of template_id → subscribed timezone
 */
export function applyEventFilter(
  events: Event[],
  filterType: FilterType,
  rsvpEventIds: Set<string>,
  followedUserIds?: Set<string>,
  currentUserId?: string,
  subscribedTemplateTimezones?: Map<string, string>,
  skippedEventIds?: Set<string>,
): Event[] {
  switch (filterType) {
    case 'morning':
    case 'afternoon':
    case 'evening':
      return filterByTimeOfDay(events, filterType);
    case 'my-rsvps':
      return filterByRSVP(events, rsvpEventIds, subscribedTemplateTimezones, skippedEventIds);
    case 'daily-events':
      return filterBySignature(events);
    case 'following':
      return filterByFollowing(events, followedUserIds || new Set());
    case 'hosting':
      return currentUserId ? filterByHosting(events, currentUserId) : [];
    case 'all':
    default:
      return events;
  }
}
